import * as React from 'react';

import { memoizeFunction } from '@fluentui/react/lib/Utilities';

import { textContainerClassName } from '@1js/acui-common';

const specialNewlineCharacter = '\u200b\u200b';

export type ILargeControlText = {
  line1: string;
  line2: string | null;
};

/**
 *  This method is used to format the text for a large control.  A large control
 *  in the ribbon has either one or two lines for text and sometimes an arrow.
 *  We inject line breaks into the text in order to define how multi-line
 *  text will wrap. The algorithm below first looks for a special newline character:
 *  This character is:  "\u200b\u200b" in javascript
 *  If it finds one of these, then it replaces the last one with a literal newline.
 *  If it does not find the special character, then it looks for a space ' ' and puts
 *  a newline at the last space.
 *
 *  @param text The text that needs to be "fixed". It will be rendered as plain text and is not HTMLEncoded.
 */
export const renderLargeControlText = memoizeFunction(
  (text: string): JSX.Element => {
    const { line1, line2 } = getLargeControlText(text);
    // Only have a line break if line2 is not null
    const lineBreak = !!line2;

    return (
      <span className={textContainerClassName}>
        {line1}
        {lineBreak && <br />}
        {line2 && line2.length && line2}
      </span>
    );
  }
);

export function getLargeControlText(text: string): ILargeControlText {
  let line1 = text;
  let line2 = null;

  const idxChar = text.lastIndexOf(specialNewlineCharacter);
  if (idxChar !== -1) {
    line1 = text.substr(0, idxChar);
    line2 = text.substr(idxChar + 2);
  } else {
    const idxSpace = text.lastIndexOf(' ');
    if (idxSpace !== -1) {
      line1 = text.substr(0, idxSpace);
      line2 = text.substr(idxSpace + 1);
    }
  }
  return {
    line1,
    line2,
  };
}
